import { MAX_APPLICATION_DOCS_FILES } from './applicationDocumentation.constants';
import type {
  ApplicationDocumentationFile,
  DocumentationDeleteTarget,
  DocumentationDisplayFile,
  DocumentationFileCategory,
} from './applicationDocumentation.types';

type ApplicationDocumentationFileRejectDetails = {
  files: Array<{
    errors?: string[];
  }>;
};

export const getApplicationDocumentationFileRejectErrorCodes = ({
  files,
}: ApplicationDocumentationFileRejectDetails): string[] =>
  Array.from(
    new Set(files.flatMap((file) => file.errors ?? []).filter(Boolean)),
  );

export const getApplicationDocumentationFileRejectDescription = ({
  errorCodes,
  tooManyFilesMessage,
  fallbackMessage,
}: {
  errorCodes: string[];
  tooManyFilesMessage: string;
  fallbackMessage: string;
}): string => {
  if (errorCodes.includes('TOO_MANY_FILES')) {
    return tooManyFilesMessage;
  }

  const joinedErrorCodes = errorCodes.join(', ');

  return joinedErrorCodes.length > 0 ? joinedErrorCodes : fallbackMessage;
};

export const formatAcceptedFileTypes = (accept: string): string =>
  accept
    .split(',')
    .map((extension) => extension.trim().replace(/^\./, '').toUpperCase())
    .filter(Boolean)
    .join(', ');

export const getDocumentationDialogFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const mergeDocumentationDialogFiles = ({
  previousFiles,
  newFiles,
  persistedFileCount = 0,
}: {
  previousFiles: File[];
  newFiles: File[];
  persistedFileCount?: number;
}) => {
  const maxStagedFiles = Math.max(
    0,
    MAX_APPLICATION_DOCS_FILES - persistedFileCount,
  );
  const uniqueFiles = [...previousFiles, ...newFiles].reduce<File[]>(
    (accumulated, file) => {
      const fileKey = getDocumentationDialogFileKey(file);
      const isDuplicate = accumulated.some(
        (existingFile) =>
          getDocumentationDialogFileKey(existingFile) === fileKey,
      );

      return isDuplicate ? accumulated : [...accumulated, file];
    },
    [],
  );

  return {
    files: uniqueFiles.slice(0, maxStagedFiles),
    hasTooManyFiles: uniqueFiles.length > maxStagedFiles,
  };
};

export const toDocumentationDisplayFiles = (
  files: File[],
): DocumentationDisplayFile[] =>
  files.map((file) => ({
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
  }));

export const getVisibleDocumentationFiles = ({
  files,
  stagedFiles,
  stagedDeletedFileIds,
}: {
  files: ApplicationDocumentationFile[];
  stagedFiles: File[];
  stagedDeletedFileIds: ReadonlySet<string>;
}): DocumentationDisplayFile[] => [
  ...files.filter((file) => !stagedDeletedFileIds.has(file.id)),
  ...toDocumentationDisplayFiles(stagedFiles),
];

export type StagedDocumentationFileRemoval =
  | { type: 'persisted'; fileId: string }
  | { type: 'staged'; stagedFileIndex: number };

export const resolveStagedDocumentationFileRemoval = ({
  files,
  stagedDeletedFileIds,
  fileIndex,
}: {
  files: ApplicationDocumentationFile[];
  stagedDeletedFileIds: ReadonlySet<string>;
  fileIndex: number;
}): StagedDocumentationFileRemoval => {
  const visiblePersistedFiles = files.filter(
    (file) => !stagedDeletedFileIds.has(file.id),
  );
  const fileId = visiblePersistedFiles[fileIndex]?.id;

  if (fileId) {
    return { type: 'persisted', fileId };
  }

  return {
    type: 'staged',
    stagedFileIndex: fileIndex - visiblePersistedFiles.length,
  };
};

export const resolveDocumentationDeleteSection = ({
  target,
  filesByCategory,
}: {
  target: DocumentationDeleteTarget | null;
  filesByCategory: Record<
    DocumentationFileCategory,
    ApplicationDocumentationFile[]
  >;
}): DocumentationFileCategory | null => {
  if (!target) {
    return null;
  }

  if (target.type === 'link') {
    return 'application';
  }

  const categories: DocumentationFileCategory[] = [
    'application',
    'api',
    'testing-reports',
  ];

  return (
    categories.find((category) =>
      filesByCategory[category].some((file) => file.id === target.fileId),
    ) ?? null
  );
};
